import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaComments, FaFileInvoice, FaStar, FaComment, FaCog, FaChartBar, FaClipboardCheck, FaThumbtack, FaEllipsisV, FaUsers, FaSearch, FaFilter, FaChevronUp, FaChevronDown } from "react-icons/fa";
import io from "socket.io-client";
import axios from "axios";
import Chatbox from "../Chatbox/Chatbox";
import ClientPagination from "./ClientPagination";
import ClientFilters from "./ClientFilters";
import ClientMenuModal from "./ClientMenuModal";
import EditClientModal from "./EditClientModal";
import ChecklistModal from "./ChecklistModal";
import Step1ChecklistModal from "./Step1ChecklistModal";
import PageLayout from "../../components/ui/PageLayout";
import PageHeader from "../../components/ui/PageHeader";
import SkeletonLoader from "../../components/ui/SkeletonLoader";
import EmptyState, { EmptyTableRow } from "../../components/ui/EmptyState";
import { useToast } from "../../context/ToastContext";

const BADGE_STYLES = {
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
};

const StatusBadge = ({ type = 'info', children, className = '' }) => (
  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${BADGE_STYLES[type]} ${className}`}>
    {children}
  </span>
);

const EmployeeDashboard = () => {
  const [clients, setClients] = useState([]);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatUser, setChatUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedMenuUser, setSelectedMenuUser] = useState(null);
  const [stepFilter, setStepFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showStepModal, setShowStepModal] = useState(false);
  const [selectedStepUser, setSelectedStepUser] = useState(null);
  const [selectedStep, setSelectedStep] = useState(2);
  const [userSteps, setUserSteps] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(15);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", email: "" });
  const [employee, setEmployee] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistUser, setChecklistUser] = useState(null);
  const [checklistMemberIndex, setChecklistMemberIndex] = useState(-1);
  const [checklistFormType, setChecklistFormType] = useState("");
  const [showStep1ChecklistModal, setShowStep1ChecklistModal] = useState(false);
  const [step1ChecklistUser, setStep1ChecklistUser] = useState(null);
  const [step1ChecklistMemberIndex, setStep1ChecklistMemberIndex] = useState(-1);
  const [actionsMenuUser, setActionsMenuUser] = useState(null);
  const [actionsMenuPos, setActionsMenuPos] = useState({ top: 0, left: 0 });
  const [menuInitialTab, setMenuInitialTab] = useState('services');
  const [menuInitialMemberIndex, setMenuInitialMemberIndex] = useState(-1);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  // Real-time online status tracking
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/auth/online-users`)
      .then(res => {
        if (res.data.success && Array.isArray(res.data.onlineUserIds)) {
          setOnlineUserIds(res.data.onlineUserIds);
        }
      })
      .catch(err => console.error('Error fetching online users in Employee ClientManagment:', err));

    const socket = io(process.env.REACT_APP_API_BASE_URL);
    socket.emit('joinRoom', 'admin');
    socket.emit('getOnlineUsers');

    socket.on('onlineUsersList', (userIds) => {
      setOnlineUserIds(userIds || []);
    });

    socket.on('userOnlineStatus', ({ userId, isOnline }) => {
      setOnlineUserIds(prev => {
        if (isOnline) {
          return prev.includes(userId) ? prev : [...prev, userId];
        } else {
          return prev.filter(id => id !== userId);
        }
      });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const closeActionsMenu = () => setActionsMenuUser(null);

  const openActionsMenu = (client, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    setActionsMenuPos({
      top: rect.bottom + 6,
      left: Math.max(8, rect.right - menuWidth),
    });
    setActionsMenuUser(client);
  };

  const sortClientsList = (clientsList) => {
    return [...clientsList].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const togglePin = async (clientId) => {
    const token = localStorage.getItem("employeeToken");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${clientId}/toggle-pin`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Failed to toggle pin");
      const data = await response.json();
      
      setClients(prevClients => {
        const updated = prevClients.map(c => 
          c._id === clientId ? { ...c, isPinned: data.user.isPinned } : c
        );
        return sortClientsList(updated);
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, stepFilter, searchQuery, paymentFilter, serviceFilter]);

  const fetchClients = async () => {
    const token = localStorage.getItem("employeeToken");
    if (!token) {
      navigate("/employee-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch clients");

      const data = await response.json();
      setEmployee(data.employee);
      const sortedClients = sortClientsList(data.employee.clients);
      setClients(sortedClients);
      sortedClients.forEach((client) => {
        fetchUserSteps(client._id);
        fetchUserMemberSteps(client._id, client);
      });
      setFilteredClients(sortedClients);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSteps = async (userId) => {
    const token = localStorage.getItem("employeeToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/steps`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch user steps");

      const data = await response.json();
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          steps: data.steps
        }
      }));
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchUserMemberSteps = async (userId, userObj = null) => {
    const token = localStorage.getItem("employeeToken");
    const client = userObj || clients.find((c) => c._id === userId);

    if (client && (!client.members || client.members.length === 0)) {
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: [],
        },
      }));
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/member-steps/0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch user member steps");

      const data = await response.json();
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: data.memberSteps || [],
        },
      }));
    } catch (error) {
      console.error("Error fetching member steps:", error);
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: [],
        },
      }));
    }
  };

  const filterClients = () => {
    let filtered = clients;

    if (stepFilter !== "all") {
      // Parse compound filters like "step1-completed", "total-in-progress"
      const statusMap = {
        "in-progress": "in-progress",
        "completed": "completed",
        "pending": "pending",
      };

      filtered = filtered.filter((client) => {
        const steps = userSteps[client._id]?.steps || [];
        const memberSteps = userSteps[client._id]?.memberSteps || [];
        const allSteps = [...steps, ...memberSteps];

        if (stepFilter.startsWith("total-")) {
          const status = stepFilter.replace("total-", "").replace("-", " ");
          // "total-in-progress" → "in-progress", "total-completed" → "completed"
          return allSteps.some((step) => {
            if (status === "in progress") return step.status === "in-progress";
            if (status === "completed") return step.status === "completed";
            if (status === "pending") return !step.status || step.status === "pending";
            return false;
          });
        }

        // "step1-completed" → step number 1, status "completed"
        const match = stepFilter.match(/^step(\d+)-(.+)$/);
        if (match) {
          const stepNum = parseInt(match[1]);
          const rawStatus = match[2];
          const status = rawStatus === "in-progress" ? "in-progress"
            : rawStatus === "completed" ? "completed"
            : "pending";
          return allSteps.some((step) => {
            if (step.step !== stepNum) return false;
            if (status === "pending") return !step.status || step.status === "pending";
            return step.status === status;
          });
        }

        // Legacy: plain step number
        return allSteps.some((step) =>
          step.step === parseInt(stepFilter) && step.status === "submitted"
        );
      });
    }

    if (serviceFilter !== "all") {
      filtered = filtered.filter((client) => {
        switch (serviceFilter) {
          case "registeredAgent":
            return client.isRegisteredAgentEnabled;
          case "usa":
            return client.isUSAEnabled;
          case "tax":
            return client.isTaxFilingEnabled;
          case "bookkeeping":
            return client.isBookkeepingEnabled;
          case "cfo":
            return client.isCFOEnabled;
          default:
            return true;
        }
      });
    }

    if (paymentFilter !== "all") {
      filtered = filtered.filter((client) => {
        const hasService = client.isUSAEnabled || client.isTaxFilingEnabled ||
          client.isBookkeepingEnabled || client.isCFOEnabled || client.isRegisteredAgentEnabled;
        if (paymentFilter === "paid") return hasService && client.paymentStatus === "paid";
        if (paymentFilter === "unpaid") return !hasService || client.paymentStatus !== "paid";
        if (paymentFilter === "wip-unpaid") return hasService && client.paymentStatus !== "paid";
        if (paymentFilter === "p-extension") return client.extensionStatus === "paid";
        if (paymentFilter === "up-extension") return client.extensionStatus !== "paid";
        return true;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((client) =>
        client.name.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query) ||
        (client._id && client._id.toLowerCase().includes(query)) ||
        (client.members && client.members.some(m => m.name.toLowerCase().includes(query)))
      );
    }

    setFilteredClients(filtered);
    setCurrentPage(1);
  };

  const isNewClient = (createdAt) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 3);
    return new Date(createdAt) > oneWeekAgo;
  };

  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEdit = (client) => {
    setSelectedUser(client);
    setEditFormData({ name: client.name, email: client.email });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${selectedUser._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) throw new Error("Failed to update user");

      const updatedUser = await response.json();
      setClients(clients.map((client) =>
        client._id === updatedUser.user._id ? updatedUser.user : client
      ));
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleChatbox = (user) => {
    setChatUser((prev) => (prev && prev._id === user._id ? null : user));
  };

  const openChatbox = (user) => {
    setChatUser(user);
  };

  const openClientMenu = (user, tab = 'services') => {
    setSelectedMenuUser(user);
    setSelectedStepUser(user);
    setMenuInitialTab(tab);
    setShowMenu(true);
  };

  const handleMenuClick = (user) => {
    openClientMenu(user, 'services');
  };

  const handleDocumentsClick = (user) => {
    openClientMenu(user, 'documents');
  };

  const handlePrepareChecklist = (user, memberIndex = -1, serviceType = 'tax') => {
    if (!user) {
      toast.error('Error: User data is missing');
      return;
    }
    
    let formType;
    if (serviceType === 'bookkeeping') {
      formType = 'Bookkeeping';
    } else {
      formType = memberIndex === -1 
        ? user.assignedForm 
        : (user.members && user.members[memberIndex] ? user.members[memberIndex].assignedForm : null);
    }
    
    if (!formType) {
      toast.warning('No form assigned. Please assign a form first.');
      return;
    }

    const normalizedFormType = formType === '1120-S' ? '1120S' : formType;
    
    setChecklistUser(user);
    setChecklistMemberIndex(memberIndex);
    setChecklistFormType(normalizedFormType);
    setShowChecklistModal(true);
  };

  const handleOpenStep1Checklist = (user, memberIndex = -1) => {
    if (!user) {
      toast.error('Error: User data is missing');
      return;
    }
    setStep1ChecklistUser(user);
    setStep1ChecklistMemberIndex(memberIndex);
    setShowStep1ChecklistModal(true);
  };

  const handleFormAssignment = async (userId, formNumber) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      if (selectedMenuUser?._id === data.user._id) {
        setSelectedMenuUser(data.user);
      }
      toast.success('Form assigned successfully!');
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const handleMemberFormAssignment = async (userId, memberIndex, formNumber) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-member-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign member form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      if (selectedMenuUser?._id === data.user._id) {
        setSelectedMenuUser(data.user);
      }
      toast.success('Member form assigned successfully!');
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const handleStepSubmit = async () => {
    if (!selectedStepUser) {
      setError("No user selected for step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      await fetchUserSteps(selectedStepUser._id);
      setShowStepModal(false);
      toast.success(`Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        toast.info(`Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const handleMemberStepSubmit = async (memberIndex) => {
    if (!selectedStepUser) {
      setError("No user selected for member step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-member-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            memberIndex,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit member step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      await fetchUserMemberSteps(selectedStepUser._id);
      setShowStepModal(false);
      toast.success(`Member ${memberIndex + 1} Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        toast.info(`Member ${memberIndex + 1} Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const isStepSubmitted = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    const stepInfo = userSteps[user._id].steps.find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const isMemberStepSubmitted = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    const stepInfo = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const canSubmitStep = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].steps.find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
  };

  const canSubmitMemberStep = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
  };

  // USA Company Formation functions
  const handleUSAFormationStepSubmit = async (stepNumber) => {
    if (!selectedStepUser) {
      setError("No user selected for USA formation step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: stepNumber
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit USA formation step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      
      toast.success(`USA Formation Step ${stepNumber} submitted successfully!`);

      if (data.nextStep) {
        toast.info(`Step ${data.nextStep} has been unlocked for the client!`);
      }
      
      // Refresh client data to ensure we have the latest state
      fetchClients();
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const isUSAFormationStepSubmitted = (userId, stepNumber) => {
    const client = clients.find(c => c._id === userId);
    if (!client || !client.companySteps) return false;
    
    const step = client.companySteps.find(s => s.step === stepNumber);
    return step && step.status === 'submitted';
  };

  const canSubmitUSAFormationStep = (userId, stepNumber) => {
    const client = clients.find(c => c._id === userId);
    if (!client || !client.companySteps) return false;
    
    // Step 1 can only be submitted by user
    if (stepNumber === 1) return false;
    
    // For steps 2 and 3, check if previous step is submitted
    if (stepNumber > 1) {
      const previousStep = client.companySteps.find(s => s.step === stepNumber - 1);
      if (!previousStep || previousStep.status !== 'submitted') return false;
    }
    
    const currentStep = client.companySteps.find(s => s.step === stepNumber);
    return currentStep && currentStep.status !== 'submitted';
  };

  const handleCompanyFormAssignment = async (userId, formType) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/assign-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formType }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign company form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      toast.success(`${formType} form assigned successfully!`);
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
    }
  };

  const areAllStepsCompleted = (userId) => {
    if (!userSteps[userId]?.steps) return false;

    const steps = userSteps[userId].steps;
    
    // Check if user completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4;
  };

  const areAllMemberStepsCompleted = (userId, memberIndex) => {
    if (!userSteps[userId]?.memberSteps?.[memberIndex]) return false;
    
    const steps = userSteps[userId].memberSteps[memberIndex];
    
    // Check if member completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4;
  };

  const renderStepsProgress = (userId, memberIndex = null) => {
    if (!userSteps[userId]) return null;

    const renderActiveStepBadges = (steps, keyPrefix) => {
      const activeSteps = steps.filter(
        (step) => step.status === 'in-progress' || step.status === 'client-pending'
      );

      if (activeSteps.length === 0) {
        const submittedSteps = steps.filter((s) => s.status === 'submitted');
        if (submittedSteps.length > 0) {
          return <StatusBadge type="info">In Progress</StatusBadge>;
        }
        return <StatusBadge type="warning">Not Started</StatusBadge>;
      }

      return activeSteps.map((step) => {
        const formattedStatus = step.status
          .replace('-', ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase());
        const badgeType = step.status === 'client-pending' ? 'warning' : 'danger';

        return (
          <StatusBadge key={`${keyPrefix}-${step.step}`} type={badgeType} className="mr-1">
            Step {step.step}: {formattedStatus}
          </StatusBadge>
        );
      });
    };

    if (memberIndex === null) {
      if (areAllStepsCompleted(userId)) {
        return <StatusBadge type="success">Completed</StatusBadge>;
      }

      const steps = userSteps[userId]?.steps || [];
      return renderActiveStepBadges(steps, 'primary');
    }

    if (userSteps[userId]?.memberSteps?.[memberIndex]) {
      if (areAllMemberStepsCompleted(userId, memberIndex)) {
        return <StatusBadge type="success">Completed</StatusBadge>;
      }

      const steps = userSteps[userId].memberSteps[memberIndex];
      return renderActiveStepBadges(steps, `member-${memberIndex}`);
    }

    return <StatusBadge type="warning">Not Started</StatusBadge>;
  };

  const renderUSAFormationProgress = (userId) => {
    const client = clients.find((c) => c._id === userId);
    if (!client || !client.companySteps) {
      return <StatusBadge type="warning">Not Started</StatusBadge>;
    }

    const allStepsCompleted = client.companySteps.every((step) => step.status === 'completed');
    if (allStepsCompleted && client.companySteps.length > 0) {
      return <StatusBadge type="success">Completed</StatusBadge>;
    }

    const inProgressSteps = client.companySteps.filter(
      (step) =>
        step.status === 'in-progress'
        || (step.status !== 'completed'
          && step.step > 1
          && client.companySteps.some((s) => s.step === step.step - 1 && s.status === 'completed'))
    );

    if (inProgressSteps.length === 0) {
      if (client.companySteps.some((step) => step.status === 'completed')) {
        const lastCompletedStep = Math.max(
          ...client.companySteps.filter((step) => step.status === 'completed').map((step) => step.step)
        );
        const nextStep = lastCompletedStep + 1;
        if (nextStep <= 3) {
          return <StatusBadge type="info" className="mr-1">Ready for Step {nextStep}</StatusBadge>;
        }
      }
      return <StatusBadge type="warning">Not Started</StatusBadge>;
    }

    return inProgressSteps.map((step) => (
      <StatusBadge key={`usa-formation-${step.step}`} type="danger" className="mr-1">
        Step {step.step}: {step.status === 'in-progress' ? 'in-progress' : 'ready'}
      </StatusBadge>
    ));
  };

  const handleMemberNameUpdate = async (userId, memberIndex, name) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-name`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, name }),
        }
      );

      if (!response.ok) throw new Error("Failed to update member name");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleMemberSection = async (userId, isEnabled) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-member-section`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, isEnabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle member section");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const addMember = async (userId) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/add-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add member");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      return data.user.members.length - 1;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const removeMember = async (userId, memberIndex) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/remove-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove member");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const toggleServiceLock = async (userId, serviceName, isEnabled) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-service`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, serviceName, isEnabled }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle service");
      }

      const data = await response.json();
      
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      
      if (selectedMenuUser && selectedMenuUser._id === data.user._id) {
        setSelectedMenuUser(data.user);
      }
      
      console.log(`Service ${serviceName} ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      
    } catch (error) {
      console.error("Service toggle error:", error);
      setError(error.message);
      toast.error(`Failed to ${isEnabled ? 'enable' : 'disable'} ${serviceName}: ${error.message}`);
    }
  };

  const updateClientInState = (userId, updater) => {
    setClients((prev) => prev.map((c) => (c._id === userId ? updater(c) : c)));
    if (selectedMenuUser?._id === userId) {
      setSelectedMenuUser((prev) => (prev ? updater(prev) : prev));
    }
  };

  const handleAnnualReportServiceUpdate = async (userId, state, entityType, registrationDate, yearsToAdd) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/service/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ state, entityType, registrationDate, yearsToAdd }),
      });
      if (!response.ok) throw new Error('Failed to update annual report service');
      const data = await response.json();
      updateClientInState(userId, (c) => ({ ...c, annualReportService: data.annualReportService }));
      toast.success('Annual Report Service due date updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveAnnualReportService = async (userId) => {
    if (!window.confirm('Remove the Annual Report Service setup for this client?')) return;
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/service/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to remove annual report service');
      updateClientInState(userId, (c) => ({ ...c, annualReportService: undefined }));
      toast.success('Annual Report Service setup removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBookkeepingServiceUpdate = async (userId, frequency, startDate) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/service/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ frequency, startDate }),
      });
      if (!response.ok) throw new Error('Failed to update bookkeeping service');
      const data = await response.json();
      updateClientInState(userId, (c) => ({ ...c, bookkeepingService: data.bookkeepingService }));
      toast.success('Bookkeeping Service due date updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveBookkeepingService = async (userId) => {
    if (!window.confirm('Remove the Bookkeeping Service setup for this client?')) return;
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/bookkeeping/service/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to remove bookkeeping service');
      updateClientInState(userId, (c) => ({ ...c, bookkeepingService: undefined }));
      toast.success('Bookkeeping Service setup removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRegisteredAgentDueDateUpdate = async (userId, dueDate) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-registered-agent-due-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, dueDate }),
      });
      if (!response.ok) throw new Error('Failed to update Registered Agent due date');
      const data = await response.json();
      updateClientInState(userId, (c) => ({ ...c, registeredAgentService: data.user.registeredAgentService }));
      toast.success('Registered Agent due date updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRegisteredAgentDocumentUpload = async (userId, files) => {
    if (!files || files.length === 0) return;
    const token = localStorage.getItem('employeeToken');
    const formData = new FormData();
    formData.append('userId', userId);
    for (let i = 0; i < files.length; i += 1) {
      formData.append('file', files[i]);
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/send-registered-agent-document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload Registered Agent document');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Registered Agent document uploaded successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRegisteredAgentDocumentDelete = async (userId, documentId) => {
    if (!window.confirm('Delete this document?')) return;
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/registered-agent-document/${documentId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to delete Registered Agent document');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Document deleted successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAnnualReportStepStatusChange = async (userId, step, newStatus) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const user = clients.find((c) => c._id === userId);
      if (!user) throw new Error('User not found');
      let updatedSteps = [...(user.annualReportSteps || [])];
      if (updatedSteps.length === 0) {
        updatedSteps = [
          { step: 1, title: 'Checklist', status: 'pending' },
          { step: 2, title: 'Employee (Admin) side Handle', status: 'pending' },
        ];
      }
      const stepExists = updatedSteps.some((s) => s.step === step);
      if (!stepExists) {
        updatedSteps.push({
          step,
          title: step === 1 ? 'Checklist' : 'Employee (Admin) side Handle',
          status: newStatus,
        });
      }
      const step1Submitted = updatedSteps.find((s) => s.step === 1)?.status === 'submitted';
      let resolvedStatus = newStatus;
      if (step === 2 && step1Submitted && newStatus === 'pending') {
        resolvedStatus = 'in-progress';
      }
      updatedSteps = updatedSteps.map((s) => {
        if (s.step !== step) return s;
        const updated = { ...s, status: resolvedStatus };
        if (resolvedStatus === 'submitted') {
          updated.completedAt = new Date();
        } else {
          delete updated.completedAt;
        }
        return updated;
      });
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/steps/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ steps: updatedSteps }),
      });
      if (!response.ok) throw new Error('Failed to update annual report steps');
      const data = await response.json();
      updateClientInState(userId, (c) => ({
        ...c,
        annualReportSteps: data.annualReportSteps,
        ...(data.annualReportService ? { annualReportService: data.annualReportService } : {}),
        ...(data.annualReportHistory ? { annualReportHistory: data.annualReportHistory } : {}),
      }));
      if (data.dueDateAdvanced) {
        toast.success(data.message || `Due date advanced to ${data.dueDateAdvanced.dueDateText}`);
      } else {
        toast.success('Annual report step status updated');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleReopenAnnualReportChecklist = async (userId) => {
    if (!window.confirm('Re-open the checklist for this client? They will be able to edit and submit again.')) return;
    const token = localStorage.getItem('employeeToken');
    try {
      const user = clients.find((c) => c._id === userId);
      if (!user) throw new Error('User not found');
      const updatedSteps = [...(user.annualReportSteps || [])].map((s) => (
        s.step === 1 ? { ...s, status: 'in-progress' } : s
      ));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/steps/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ steps: updatedSteps }),
      });
      if (!response.ok) throw new Error('Failed to re-open checklist');
      const data = await response.json();
      updateClientInState(userId, (c) => ({ ...c, annualReportSteps: data.annualReportSteps }));
      toast.success('Checklist re-opened for the client');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const refreshUserSteps = async (userId) => {
    await fetchUserSteps(userId);
    await fetchUserMemberSteps(userId);
  };

  const getStep3Status = (userId, memberIndex) => {
    if (!userSteps[userId]) return null;
    const steps = memberIndex >= 0
      ? userSteps[userId]?.memberSteps?.[memberIndex]
      : userSteps[userId]?.steps;
    if (!steps) return null;
    const step3 = steps.find((s) => s.step === 3);
    return step3?.status || null;
  };

  const handleTaxDateUpdate = async (userId, taxYearEndDate, dueDate, clearDates = false) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-tax-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, taxYearEndDate, dueDate, clearDates }),
      });
      if (!response.ok) throw new Error('Failed to update tax dates');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Tax dates updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMemberTaxDateUpdate = async (userId, memberIndex, taxYearEndDate, dueDate, clearDates = false) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-tax-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, memberIndex, taxYearEndDate, dueDate, clearDates }),
      });
      if (!response.ok) throw new Error('Failed to update member tax dates');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Member tax dates updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePaymentStatusUpdate = async (userId, memberIndex, newStatus) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-payment-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId,
          memberIndex: memberIndex === -1 ? null : memberIndex,
          paymentStatus: newStatus,
        }),
      });
      if (!response.ok) throw new Error('Failed to update payment status');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Payment status updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMoveToHistory = async (userId, memberIndex, newTaxYearEndDate, newDueDate) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/move-to-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, memberIndex, newTaxYearEndDate, newDueDate }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to move to history');
      }
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      await refreshUserSteps(userId);
      toast.success('Tax filing moved to history and new cycle locked!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUnlockTaxFiling = async (userId, memberIndex) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/unlock-tax-filing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, memberIndex, paymentId: 'admin_override' }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to unlock tax filing');
      }
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('Tax filing unlocked successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAnnualReportPaymentStatusUpdate = async (userId, paymentStatus) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-annual-report-payment-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId,
          paymentStatus,
          paymentId: paymentStatus === 'paid' ? 'admin_override' : undefined,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update annual report payment status');
      }
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success(data.message || 'Annual Report payment status updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateTaxSummaryTable = async (userId, memberIndex, table) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/tax-summary-table`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId,
          memberIndex: memberIndex === undefined ? null : memberIndex,
          table,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to update Tax Filing table');
      updateClientInState(userId, () => data.user);
      toast.success('Tax Filing table saved successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleHistoryTaxDateUpdate = async (userId, memberIndex, historyIndex, taxYearEndDate, dueDate, clearDates = false) => {
    const token = localStorage.getItem('employeeToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-history-tax-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, memberIndex, historyIndex, taxYearEndDate, dueDate, clearDates }),
      });
      if (!response.ok) throw new Error('Failed to update history tax dates');
      const data = await response.json();
      updateClientInState(userId, () => data.user);
      toast.success('History tax dates updated successfully!');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const renderFeedbackRating = (feedback, title = 'Feedback') => {
    if (!feedback?.rating) {
      return <span className="text-sm text-gray-400">No rating</span>;
    }

    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <FaStar
            key={i}
            className={`w-3.5 h-3.5 ${i < feedback.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-neutral-600'}`}
          />
        ))}
        {feedback.comment && (
          <FaComment
            className="ml-2 text-gray-400 cursor-pointer w-3.5 h-3.5 hover:text-primary-500"
            title="View Comment"
            onClick={() => toast.info(`${title}: ${feedback.comment}`)}
          />
        )}
      </div>
    );
  };

  const renderServiceRating = (client, serviceKey) => {
    if (serviceKey === 'isUSAEnabled') {
      return renderFeedbackRating(client.usaFeedback, 'USA Formation Feedback');
    }

    if (serviceKey === 'isTaxFilingEnabled') {
      return renderFeedbackRating(client.feedback, 'Tax Filing Feedback');
    }

    if (serviceKey === 'isBookkeepingEnabled') {
      return renderFeedbackRating(client.bookkeepingFeedback, 'Bookkeeping Feedback');
    }

    return <span className="text-sm text-gray-400">No rating</span>;
  };

  const handleRowClick = (client, serviceKey = 'services', e, memberIndex = -1) => {
    if (e.target.closest('button, input, select, a, [role="button"]')) return;
    const tabMap = {
      isUSAEnabled: 'usa-formation',
      isTaxFilingEnabled: 'primary',
      isBookkeepingEnabled: 'bookkeeping',
      isCFOEnabled: 'services',
      isRegisteredAgentEnabled: 'registered-agent',
      isAnnualReportServiceEnabled: 'annual-report',
      member: 'primary'
    };
    setSelectedMenuUser(client);
    setMenuInitialTab(tabMap[serviceKey] || serviceKey || 'services');
    setMenuInitialMemberIndex(memberIndex);
    setShowMenu(true);
  };

  const renderClientRows = () => {
    if (currentClients.length === 0) {
      return (
        <EmptyTableRow
          colSpan={9}
          preset={searchQuery || stepFilter !== 'all' || paymentFilter !== 'all' || serviceFilter !== 'all' ? 'search' : 'default'}
          title={clients.length === 0 ? 'No Clients Assigned' : 'No Matching Clients'}
          description={clients.length === 0 ? 'You have no clients assigned yet.' : 'Try adjusting your search or filters.'}
        />
      );
    }

    return currentClients.map((client, index) => {
      const absoluteIndex = indexOfFirstClient + index;

      return (
        <React.Fragment key={client._id}>
          <tr
            onClick={(e) => handleRowClick(client, 'services', e)}
            className={`group bg-white dark:bg-neutral-900 border-b border-gray-100 dark:border-neutral-800 transition-all duration-200 hover:bg-primary-50/50 dark:hover:bg-primary-900/20 cursor-pointer ${
              index > 0 ? 'border-t-2 border-gray-200/60 dark:border-neutral-700' : ''
            }`}
          >
            <td className="pl-4 pr-1 py-3 text-sm text-gray-600 dark:text-gray-400 w-12">{absoluteIndex + 1}</td>
            <td className="px-4 py-3 min-w-[250px]">
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  {client.profilePicture ? (
                    <img
                      src={client.profilePicture}
                      alt={client.name}
                      className="w-10 h-10 rounded-full object-cover border-2 border-primary-100 dark:border-primary-900/40"
                      onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=random&color=fff`; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold text-lg">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {onlineUserIds.includes(client._id) && (
                    <span
                      className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-neutral-900 rounded-full animate-pulse"
                      title="Online Now"
                    />
                  )}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2 flex-wrap">
                  {client.name}
                  {onlineUserIds.includes(client._id) && (
                    <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full border border-emerald-200/50 dark:border-emerald-800/50">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                      Active
                    </span>
                  )}
                  {isNewClient(client.createdAt) && (
                      <StatusBadge type="primary">New</StatusBadge>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(client._id);
                    }}
                      className={`focus:outline-none transition-all duration-200 hover:scale-110 ${
                        client.isPinned
                          ? 'text-primary-500 dark:text-primary-400 opacity-100'
                          : 'text-gray-300 dark:text-neutral-600 hover:text-gray-400 opacity-0 group-hover:opacity-100'
                      }`}
                      title={client.isPinned ? 'Unpin Client' : 'Pin Client'}
                    >
                      <FaThumbtack className={`w-3.5 h-3.5 ${client.isPinned ? '' : '-rotate-45'}`} />
                  </button>
                </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 truncate">{client.email}</span>
              </div>
              </div>
            </td>
            <td colSpan={6} className="px-4 py-3 text-sm text-gray-400 dark:text-gray-500">—</td>
            <td className="px-4 py-3 min-w-[120px] sticky right-0 bg-white dark:bg-neutral-900 z-10">
              <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 overflow-hidden shadow-sm">
                {/* Chatbox Icon Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openChatbox(client);
                  }}
                  className="px-3 py-2 text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-all border-r border-gray-200 dark:border-neutral-700 cursor-pointer"
                  title="Open Chat"
                >
                  <img src="/chat-icon.png" className="w-4 h-4 object-contain" alt="Chat" />
                </button>

                {/* 3 Dots Actions Button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (actionsMenuUser?._id === client._id) {
                      closeActionsMenu();
                    } else {
                      openActionsMenu(client, e);
                    }
                  }}
                  className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-all cursor-pointer"
                  title="More Actions"
                >
                  <FaEllipsisV className="w-3.5 h-3.5" />
                </button>
              </div>
            </td>
          </tr>

          {[
            { key: 'isUSAEnabled', name: 'USA Company' },
            { key: 'isTaxFilingEnabled', name: 'Tax Filing' },
            { key: 'isBookkeepingEnabled', name: 'Bookkeeping' },
            { key: 'isCFOEnabled', name: 'CFO Advisory' },
            { key: 'isRegisteredAgentEnabled', name: 'Registered Agent' },
          ].map((service) => (
            client[service.key] && (
              <tr
                key={`${client._id}-${service.key}`}
                onClick={(e) => handleRowClick(client, service.key, e)}
                className="bg-gray-50/50 dark:bg-neutral-800/30 border-b border-gray-100 dark:border-neutral-800 transition-colors hover:bg-primary-50/40 dark:hover:bg-primary-900/20 cursor-pointer"
              >
                <td className="pl-4 pr-1 py-3 w-12" />
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 pl-4 border-l-2 border-primary-400 dark:border-primary-500">
                    {client.name} - {service.name}
                  </span>
                </td>
                <td className="px-4 py-3 min-w-[80px]">
                  <StatusBadge type="success">Paid</StatusBadge>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 min-w-[130px] whitespace-nowrap">{service.name}</td>
                <td className="px-4 py-3 min-w-[100px] whitespace-nowrap">
                  {service.key === 'isTaxFilingEnabled' && client.assignedForm ? (
                    <StatusBadge type="primary">{client.assignedForm}</StatusBadge>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 min-w-[140px]">
                  {renderServiceRating(client, service.key)}
                </td>
                <td className="px-4 py-3 min-w-[150px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    {service.key === 'isTaxFilingEnabled'
                      ? renderStepsProgress(client._id)
                      : service.key === 'isUSAEnabled'
                        ? renderUSAFormationProgress(client._id)
                        : '—'}
                    </div>
                </td>
                <td className="px-4 py-3 min-w-[120px] whitespace-nowrap">
                  {service.key === 'isTaxFilingEnabled' && client.assignedForm && (
                    <button
                      type="button"
                      onClick={() => handlePrepareChecklist(client, -1, 'tax')}
                      className="px-2 py-1 text-xs font-medium border border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-1 whitespace-nowrap"
                    >
                      <FaClipboardCheck /> Prepare
                    </button>
                  )}
                  {service.key === 'isBookkeepingEnabled' && (
                    <button
                      type="button"
                      onClick={() => handlePrepareChecklist(client, -1, 'bookkeeping')}
                      className="px-2 py-1 text-xs font-medium border border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-1 whitespace-nowrap"
                    >
                      <FaClipboardCheck /> Prepare
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 min-w-[120px] sticky right-0 bg-gray-50/50 dark:bg-neutral-800/30">—</td>
              </tr>
            )
          ))}

          {client.members?.map((member, memberIndex) => (
            <tr
              key={`${client._id}-member-${memberIndex}`}
              onClick={(e) => handleRowClick(client, 'member', e, memberIndex)}
              className="bg-blue-50/30 dark:bg-blue-900/5 border-b border-gray-100 dark:border-neutral-800 transition-colors hover:bg-blue-100/50 dark:hover:bg-blue-900/30 cursor-pointer"
            >
              <td className="pl-4 pr-1 py-1 w-12" />
              <td className="px-4 py-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 pl-4 border-l-2 border-primary-400 dark:border-primary-500">
                  {(() => {
                    const memberName = member.name || `Member ${memberIndex + 1}`;
                    return memberName.toLowerCase().includes('tax filing') ? memberName : `${memberName} - Tax Filing`;
                  })()}
                </span>
              </td>
              <td className="px-4 py-3 min-w-[80px]">
                <StatusBadge type="success">Paid</StatusBadge>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 min-w-[130px] whitespace-nowrap">Tax Filing</td>
              <td className="px-4 py-3 min-w-[100px] whitespace-nowrap">
                {member.assignedForm ? (
                  <StatusBadge type="primary">{member.assignedForm}</StatusBadge>
                ) : (
                  <span className="text-sm text-gray-400">Not assigned</span>
                )}
              </td>
              <td className="px-4 py-3 min-w-[140px]">
                {renderFeedbackRating(member.feedback, `Member Feedback - ${member.name || `Member ${memberIndex + 1}`}`)}
              </td>
              <td className="px-4 py-3 min-w-[150px]">
                {client.isTaxFilingEnabled ? renderStepsProgress(client._id, memberIndex) : '—'}
              </td>
              <td className="px-4 py-3 min-w-[120px] whitespace-nowrap">
                {client.isTaxFilingEnabled && member.assignedForm && (
                  <button
                    type="button"
                    onClick={() => handlePrepareChecklist(client, memberIndex, 'tax')}
                    className="px-2 py-1 text-xs font-medium border border-primary-500 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-1 whitespace-nowrap"
                  >
                    <FaClipboardCheck /> Prepare
                  </button>
                )}
              </td>
              <td className="px-4 py-3 sticky right-0 bg-blue-50/30 dark:bg-blue-900/5" />
            </tr>
          ))}
        </React.Fragment>
      );
    });
  };

  return (
    <PageLayout>
      {/* Page Header */}
      <PageHeader
        title="Client Management"
        description={`${filteredClients.length} clients assigned`}
        icon={FaUsers}
        className="mb-4 px-1"
        actions={
          <>
            {/* Search Input */}
            <div className="relative w-64 mr-2">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, email or client ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white dark:bg-neutral-850 border border-gray-200 dark:border-neutral-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all shadow-sm"
              />
            </div>

            {/* Filters Toggle Button */}
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
                showFiltersPanel
                  ? "bg-primary-600 text-white shadow-lg shadow-primary-500/30 hover:bg-primary-700"
                  : "bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700"
              }`}
            >
              <FaFilter className="w-3.5 h-3.5" />
              Filters
              {showFiltersPanel
                ? <FaChevronUp className="w-3 h-3" />
                : <FaChevronDown className="w-3 h-3" />}
            </button>
          </>
        }
      />

      {loading ? (
        <SkeletonLoader variant="table" rows={10} cols={6} />
      ) : error ? (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl border border-red-100 dark:border-red-900/30">
          {error}
        </div>
      ) : (
        <>
          {showFiltersPanel && (
            <ClientFilters
              stepFilter={stepFilter}
              setStepFilter={setStepFilter}
              paymentFilter={paymentFilter}
              setPaymentFilter={setPaymentFilter}
              serviceFilter={serviceFilter}
              setServiceFilter={setServiceFilter}
            />
          )}

          {filteredClients.length === 0 && !searchQuery && stepFilter === 'all' && paymentFilter === 'all' && serviceFilter === 'all' ? (
            <div className="card-3d">
              <EmptyState
                preset="default"
                title="No Clients Assigned"
                description="You don't have any clients assigned to you yet."
              />
            </div>
          ) : (
            <div className="mt-2 bg-white dark:bg-neutral-900 rounded-2xl shadow-card border border-gray-100 dark:border-neutral-800">
              <div className="overflow-x-auto rounded-2xl">
                <table className="w-full min-w-[1000px] table-fixed">
                  <colgroup>
                    <col className="w-12" />
                    <col className="w-[28%]" />
                    <col className="w-[8%]" />
                    <col className="w-[14%]" />
                    <col className="w-[10%]" />
                    <col className="w-[12%]" />
                    <col className="w-[14%]" />
                    <col className="w-[10%]" />
                    <col className="w-[120px]" />
                  </colgroup>
                  <thead className="bg-gray-50 dark:bg-neutral-800">
                    <tr>
                      <th className="pl-4 pr-1 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">No.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Client Information</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">P / U</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Form</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Rating</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Steps WIP</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Checklist</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase sticky right-0 bg-gray-50 dark:bg-neutral-800">Actions</th>
                </tr>
              </thead>
                  <tbody>{renderClientRows()}</tbody>
                </table>
          </div>
            </div>
          )}

          {filteredClients.length > 0 && (
          <ClientPagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
            filteredClients={filteredClients}
            clientsPerPage={clientsPerPage}
            indexOfFirstClient={indexOfFirstClient}
            indexOfLastClient={indexOfLastClient}
          />
          )}
        </>
      )}

      <EditClientModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        selectedUser={selectedUser}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        handleEditSubmit={handleEditSubmit}
      />

      <ClientMenuModal
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        initialTab={menuInitialTab}
        clients={clients}
        onlineUserIds={onlineUserIds}
        selectedMenuUser={selectedMenuUser}
        setSelectedMenuUser={setSelectedMenuUser}
        handleFormAssignment={handleFormAssignment}
        handleMemberFormAssignment={handleMemberFormAssignment}
        handleStepSubmit={handleStepSubmit}
        handleMemberStepSubmit={handleMemberStepSubmit}
        isStepSubmitted={isStepSubmitted}
        isMemberStepSubmitted={isMemberStepSubmitted}
        canSubmitStep={canSubmitStep}
        canSubmitMemberStep={canSubmitMemberStep}
        toggleServiceLock={toggleServiceLock}
        handleMemberNameUpdate={handleMemberNameUpdate}
        addMember={addMember}
        removeMember={removeMember}
        onChatClick={(user) => openChatbox(user)}
        handleAnnualReportServiceUpdate={handleAnnualReportServiceUpdate}
        handleRemoveAnnualReportService={handleRemoveAnnualReportService}
        handleBookkeepingServiceUpdate={handleBookkeepingServiceUpdate}
        handleRemoveBookkeepingService={handleRemoveBookkeepingService}
        handleRegisteredAgentDueDateUpdate={handleRegisteredAgentDueDateUpdate}
        handleRegisteredAgentDocumentUpload={handleRegisteredAgentDocumentUpload}
        handleRegisteredAgentDocumentDelete={handleRegisteredAgentDocumentDelete}
        handleAnnualReportStepStatusChange={handleAnnualReportStepStatusChange}
        handleReopenAnnualReportChecklist={handleReopenAnnualReportChecklist}
        userSteps={userSteps}
        refreshUserSteps={refreshUserSteps}
        handleTaxDateUpdate={handleTaxDateUpdate}
        handleMemberTaxDateUpdate={handleMemberTaxDateUpdate}
        handlePaymentStatusUpdate={handlePaymentStatusUpdate}
      handleMoveToHistory={handleMoveToHistory}
        handleUnlockTaxFiling={handleUnlockTaxFiling}
        handleAnnualReportPaymentStatusUpdate={handleAnnualReportPaymentStatusUpdate}
      handleUpdateTaxSummaryTable={handleUpdateTaxSummaryTable}
      handleHistoryTaxDateUpdate={handleHistoryTaxDateUpdate}
      handleOpenStep1Checklist={handleOpenStep1Checklist}
        onPrepareChecklist={handlePrepareChecklist}
        getStep3Status={getStep3Status}
        updateClientFromUser={updateClientInState}
        initialMemberIndex={menuInitialMemberIndex}
      />

      {actionsMenuUser && typeof document !== 'undefined' && createPortal(
        <>
          <div className="fixed inset-0 z-[1100]" onClick={closeActionsMenu} aria-hidden="true" />
          <div
            className="fixed z-[1101] w-44 bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-gray-200 dark:border-neutral-700 py-1 animate-fade-in"
            style={{ top: actionsMenuPos.top, left: actionsMenuPos.left }}
          >
            <div className="px-4 py-2 border-b border-gray-100 dark:border-neutral-800">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{actionsMenuUser.name}</p>
            </div>
            <button
              type="button"
              onClick={() => { openChatbox(actionsMenuUser); closeActionsMenu(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800"
            >
              <img src="/chat-icon.png" className="w-4 h-4 object-contain shrink-0" alt="Chat" /> Chat
            </button>
            <button
              type="button"
              onClick={() => { handleDocumentsClick(actionsMenuUser); closeActionsMenu(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800"
            >
              <FaFileInvoice className="w-4 h-4 text-primary-500 shrink-0" /> Documents
            </button>
            <button
              type="button"
              onClick={() => { handleMenuClick(actionsMenuUser); closeActionsMenu(); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800"
            >
              <FaCog className="w-4 h-4 text-primary-500 shrink-0" /> Menu
            </button>
          </div>
        </>,
        document.body
      )}

      {chatUser && (
        <Chatbox
          user={chatUser}
          onClose={() => setChatUser(null)}
        />
      )}

      <ChecklistModal
        show={showChecklistModal}
        onHide={() => setShowChecklistModal(false)}
        userId={checklistUser?._id}
        memberIndex={checklistMemberIndex}
        formType={checklistFormType}
      />

      <Step1ChecklistModal
        show={showStep1ChecklistModal}
        onClose={() => {
          setShowStep1ChecklistModal(false);
          setStep1ChecklistUser(null);
          setStep1ChecklistMemberIndex(-1);
        }}
        userId={step1ChecklistUser?._id}
        memberIndex={step1ChecklistMemberIndex}
        onSaved={() => {
          if (step1ChecklistUser?._id) {
            refreshUserSteps(step1ChecklistUser._id);
          }
        }}
      />
    </PageLayout>
  );
};

export default EmployeeDashboard;